import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  Radio,
  InputNumber,
  message,
  Popconfirm,
  Checkbox,
  Tabs,
  Descriptions,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  ExportOutlined,
  UserOutlined,
  TagsOutlined,
  LockOutlined,
  UnlockOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// 会员接口
interface Member {
  id: number;
  memberId: number; // 会员ID
  account: string; // 会员账号
  nickname?: string; // 会员昵称
  accountStatus: 'normal' | 'locked' | 'frozen'; // 账号状态
  superiorAgentAccount: string; // 上级代理账号
  superiorAgentId: number; // 上级代理ID
  vipLevel: number; // VIP等级
  balance: number; // 会员余额
  totalRecharge: number; // 总充值
  totalWithdrawal: number; // 总提现
  depositCount: number; // 存款次数
  withdrawalCount: number; // 提现次数
  totalBet: number; // 总投注
  totalWinLoss: number; // 总输赢
  registerTime: string; // 注册时间
  firstDepositTime?: string; // 首充时间
  discount: number; // 优惠
  rebate: number; // 返水
  commission: number; // 返佣
  mobile?: string; // 会员手机号码
  remarks?: string; // 备注
  tags?: MemberTag[]; // 会员标签
}

// 会员标签接口
interface MemberTag {
  tagId: number;
  tagName: string;
  tagColor: string;
  tagGroupName: string;
  lastModifier: string; // 最后修改人
  lastModifyTime: string; // 最后修改时间
}

// 模拟标签数据（从用户标签管理获取）
const mockTags = [
  { id: 1, name: 'VIP1', color: '#ff4d4f', tagGroupName: '用户等级' },
  { id: 2, name: 'VIP2', color: '#ff7a45', tagGroupName: '用户等级' },
  { id: 3, name: '活跃用户', color: '#52c41a', tagGroupName: '用户状态' },
  { id: 4, name: '沉默用户', color: '#8c8c8c', tagGroupName: '用户状态' },
  { id: 5, name: '高价值用户', color: '#1890ff', tagGroupName: '用户特征' },
  { id: 6, name: '风险用户', color: '#faad14', tagGroupName: '用户特征' },
];

// 模拟会员数据
const mockMembers: Member[] = [
  {
    id: 1,
    memberId: 258630,
    account: 'qa096329946',
    nickname: 'aa5434ptld',
    accountStatus: 'normal',
    superiorAgentAccount: 'system',
    superiorAgentId: 0,
    vipLevel: 0,
    balance: 1039299465.96,
    totalRecharge: 0,
    totalWithdrawal: 0,
    depositCount: 0,
    withdrawalCount: 0,
    totalBet: 940000,
    totalWinLoss: 1509000,
    registerTime: '2025-10-24 10:24:48',
    firstDepositTime: undefined,
    discount: 0,
    rebate: 0,
    commission: 0,
    tags: [
      { tagId: 1, tagName: 'VIP1', tagColor: '#ff4d4f', tagGroupName: '用户等级', lastModifier: 'admin', lastModifyTime: '2025-11-20 10:00:00' },
      { tagId: 5, tagName: '高价值用户', tagColor: '#1890ff', tagGroupName: '用户特征', lastModifier: 'admin', lastModifyTime: '2025-11-20 11:00:00' },
    ],
  },
];

const MemberList: React.FC = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [members, setMembers] = useState<Member[]>(mockMembers);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [batchTagModalVisible, setBatchTagModalVisible] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [activeTab, setActiveTab] = useState('userInfo');
  const [filters, setFilters] = useState<any>({});

  // 处理搜索
  const handleSearch = () => {
    const values = form.getFieldsValue();
    setFilters(values);
    message.info('搜索完成');
  };

  // 处理重置
  const handleReset = () => {
    form.resetFields();
    setFilters({});
    message.info('已重置');
  };

  // 处理导出
  const handleExport = () => {
    message.success('导出成功');
  };

  // 处理新增
  const handleAdd = () => {
    message.info('新增会员');
  };

  // 处理允许登录
  const handleAllowLogin = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要操作的会员');
      return;
    }
    message.success(`已允许 ${selectedRowKeys.length} 个会员登录`);
  };

  // 处理禁止登录
  const handleForbidLogin = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要操作的会员');
      return;
    }
    message.success(`已禁止 ${selectedRowKeys.length} 个会员登录`);
  };

  // 处理资金解冻
  const handleUnfreezeFunds = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要操作的会员');
      return;
    }
    message.success(`已解冻 ${selectedRowKeys.length} 个会员的资金`);
  };

  // 处理资金冻结
  const handleFreezeFunds = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要操作的会员');
      return;
    }
    message.success(`已冻结 ${selectedRowKeys.length} 个会员的资金`);
  };

  // 处理批量打标签
  const handleBatchTag = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要打标签的会员');
      return;
    }
    setBatchTagModalVisible(true);
  };

  // 处理编辑
  const handleEdit = (member: Member) => {
    setEditingMember(member);
    editForm.resetFields();
    editForm.setFieldsValue({
      account: member.account,
      memberId: member.memberId,
      nickname: member.nickname,
      email: '',
      mobile: member.mobile || '',
      realName: '',
      vipLevel: member.vipLevel,
      accountStatus: member.accountStatus,
      isLocked: false,
      remarks: member.remarks || '',
      blacklistBan: '',
      superiorAgent: member.superiorAgentAccount,
      superiorAgentId: member.superiorAgentId,
      agentAccount: member.account,
      level: '一级代理',
      directSubordinates: 0,
      allSubordinates: 0,
      withdrawalAccount: member.account,
      memberTags: member.tags?.map(t => t.tagId) || [],
    });
    setActiveTab('userInfo');
    setEditModalVisible(true);
  };

  // 处理保存编辑
  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      const updatedTags = (values.memberTags || []).map((tagId: number) => {
        const tag = mockTags.find(t => t.id === tagId);
        if (tag) {
          return {
            tagId: tag.id,
            tagName: tag.name,
            tagColor: tag.color,
            tagGroupName: tag.tagGroupName,
            lastModifier: 'admin',
            lastModifyTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          };
        }
        return null;
      }).filter(Boolean) as MemberTag[];

      setMembers(prev => prev.map(item => {
        if (item.id === editingMember?.id) {
          return {
            ...item,
            ...values,
            tags: updatedTags,
          };
        }
        return item;
      }));

      message.success('会员信息已更新');
      setEditModalVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // 处理批量打标签提交
  const handleBatchTagSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      const selectedTags = (values.batchTags || []).map((tagId: number) => {
        const tag = mockTags.find(t => t.id === tagId);
        if (tag) {
          return {
            tagId: tag.id,
            tagName: tag.name,
            tagColor: tag.color,
            tagGroupName: tag.tagGroupName,
            lastModifier: 'admin',
            lastModifyTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          };
        }
        return null;
      }).filter(Boolean) as MemberTag[];

      setMembers(prev => prev.map(item => {
        if (selectedRowKeys.includes(item.id)) {
          // 合并标签，去重
          const existingTagIds = item.tags?.map(t => t.tagId) || [];
          const newTags = selectedTags.filter(t => !existingTagIds.includes(t.tagId));
          return {
            ...item,
            tags: [...(item.tags || []), ...newTags],
          };
        }
        return item;
      }));

      message.success(`已为 ${selectedRowKeys.length} 个会员打标签`);
      setBatchTagModalVisible(false);
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const columns = [
    {
      title: '',
      key: 'checkbox',
      width: 50,
      render: () => null,
    },
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '会员账号',
      dataIndex: 'account',
      key: 'account',
      width: 150,
    },
    {
      title: '会员ID',
      dataIndex: 'memberId',
      key: 'memberId',
      width: 120,
      sorter: (a: Member, b: Member) => a.memberId - b.memberId,
    },
    {
      title: '账号状态',
      dataIndex: 'accountStatus',
      key: 'accountStatus',
      width: 100,
      render: (status: string) => {
        const statusMap: Record<string, { text: string; color: string }> = {
          normal: { text: '正常', color: 'success' },
          locked: { text: '锁定', color: 'error' },
          frozen: { text: '冻结', color: 'warning' },
        };
        const statusInfo = statusMap[status] || statusMap.normal;
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '上级代理账号',
      dataIndex: 'superiorAgentAccount',
      key: 'superiorAgentAccount',
      width: 150,
    },
    {
      title: '上级代理ID',
      dataIndex: 'superiorAgentId',
      key: 'superiorAgentId',
      width: 120,
      sorter: (a: Member, b: Member) => a.superiorAgentId - b.superiorAgentId,
    },
    {
      title: 'VIP等级',
      dataIndex: 'vipLevel',
      key: 'vipLevel',
      width: 100,
      render: (level: number) => `VIP${level}`,
      sorter: (a: Member, b: Member) => a.vipLevel - b.vipLevel,
    },
    {
      title: '会员余额',
      dataIndex: 'balance',
      key: 'balance',
      width: 150,
      render: (amount: number) => amount.toLocaleString(),
      sorter: (a: Member, b: Member) => a.balance - b.balance,
    },
    {
      title: '总充值',
      dataIndex: 'totalRecharge',
      key: 'totalRecharge',
      width: 120,
      render: (amount: number) => amount.toLocaleString(),
      sorter: (a: Member, b: Member) => a.totalRecharge - b.totalRecharge,
    },
    {
      title: '总提现',
      dataIndex: 'totalWithdrawal',
      key: 'totalWithdrawal',
      width: 120,
      render: (amount: number) => amount.toLocaleString(),
      sorter: (a: Member, b: Member) => a.totalWithdrawal - b.totalWithdrawal,
    },
    {
      title: '存款次数',
      dataIndex: 'depositCount',
      key: 'depositCount',
      width: 100,
      sorter: (a: Member, b: Member) => a.depositCount - b.depositCount,
    },
    {
      title: '提现次数',
      dataIndex: 'withdrawalCount',
      key: 'withdrawalCount',
      width: 100,
      sorter: (a: Member, b: Member) => a.withdrawalCount - b.withdrawalCount,
    },
    {
      title: '总投注',
      dataIndex: 'totalBet',
      key: 'totalBet',
      width: 120,
      render: (amount: number) => amount.toLocaleString(),
      sorter: (a: Member, b: Member) => a.totalBet - b.totalBet,
    },
    {
      title: '总输赢',
      dataIndex: 'totalWinLoss',
      key: 'totalWinLoss',
      width: 120,
      render: (amount: number) => amount.toLocaleString(),
      sorter: (a: Member, b: Member) => a.totalWinLoss - b.totalWinLoss,
    },
    {
      title: '注册时间',
      dataIndex: 'registerTime',
      key: 'registerTime',
      width: 170,
    },
    {
      title: '首充时间',
      dataIndex: 'firstDepositTime',
      key: 'firstDepositTime',
      width: 170,
      render: (time: string) => time || '-',
    },
    {
      title: '优惠',
      dataIndex: 'discount',
      key: 'discount',
      width: 100,
      render: (amount: number) => amount.toLocaleString(),
      sorter: (a: Member, b: Member) => a.discount - b.discount,
    },
    {
      title: '返水',
      dataIndex: 'rebate',
      key: 'rebate',
      width: 100,
      render: (amount: number) => amount.toLocaleString(),
      sorter: (a: Member, b: Member) => a.rebate - b.rebate,
    },
    {
      title: '返佣',
      dataIndex: 'commission',
      key: 'commission',
      width: 100,
      render: (amount: number) => amount.toLocaleString(),
      sorter: (a: Member, b: Member) => a.commission - b.commission,
    },
    {
      title: '会员手机号码',
      dataIndex: 'mobile',
      key: 'mobile',
      width: 130,
      render: (mobile: string) => mobile || '-',
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 150,
      ellipsis: true,
    },
    {
      title: '会员标签',
      key: 'tags',
      width: 200,
      render: (_: any, record: Member) => (
        <Space wrap>
          {record.tags?.map(tag => (
            <Tag key={tag.tagId} color={tag.tagColor}>
              {tag.tagName}
            </Tag>
          )) || '-'}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: Member) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
          编辑
        </Button>
      ),
    },
  ];

  // 筛选会员数据
  const filteredMembers = members.filter(member => {
    // 标签筛选
    if (filters.memberTags && filters.memberTags.length > 0) {
      const memberTagIds = member.tags?.map(t => t.tagId) || [];
      const hasAllTags = filters.memberTags.every((tagId: number) => memberTagIds.includes(tagId));
      if (!hasAllTags) return false;
    }

    // 会员ID筛选
    if (filters.memberId && member.memberId.toString() !== filters.memberId) {
      return false;
    }

    // 会员账号筛选
    if (filters.memberAccount && !member.account.toLowerCase().includes(filters.memberAccount.toLowerCase())) {
      return false;
    }

    // 上级代理ID筛选
    if (filters.superiorAgentId && member.superiorAgentId.toString() !== filters.superiorAgentId) {
      return false;
    }

    // 上级代理账号筛选
    if (filters.superiorAgentAccount && !member.superiorAgentAccount.toLowerCase().includes(filters.superiorAgentAccount.toLowerCase())) {
      return false;
    }

    // VIP等级筛选
    if (filters.vipLevel !== undefined && filters.vipLevel !== null && member.vipLevel !== filters.vipLevel) {
      return false;
    }

    // 手机号码筛选
    if (filters.mobile && (!member.mobile || !member.mobile.includes(filters.mobile))) {
      return false;
    }

    // 账户状态筛选
    if (filters.accountStatus && member.accountStatus !== filters.accountStatus) {
      return false;
    }

    // 充值状态筛选
    if (filters.rechargeStatus) {
      if (filters.rechargeStatus === 'yes' && member.totalRecharge === 0) {
        return false;
      }
      if (filters.rechargeStatus === 'no' && member.totalRecharge > 0) {
        return false;
      }
    }

    // 注册时间筛选
    if (filters.registerTimeRange && filters.registerTimeRange.length === 2) {
      const registerTime = dayjs(member.registerTime);
      const startTime = filters.registerTimeRange[0];
      const endTime = filters.registerTimeRange[1];
      if (registerTime.isBefore(startTime) || registerTime.isAfter(endTime)) {
        return false;
      }
    }

    // 首充时间筛选
    if (filters.firstDepositTimeRange && filters.firstDepositTimeRange.length === 2 && member.firstDepositTime) {
      const firstDepositTime = dayjs(member.firstDepositTime);
      const startTime = filters.firstDepositTimeRange[0];
      const endTime = filters.firstDepositTimeRange[1];
      if (firstDepositTime.isBefore(startTime) || firstDepositTime.isAfter(endTime)) {
        return false;
      }
    }

    return true;
  });

  return (
    <div style={{ padding: 24 }}>
      <Card>
        {/* 搜索筛选区域 */}
        <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
          <Row gutter={16} style={{ width: '100%' }}>
            <Col span={8}>
              <Form.Item label="注册时间" name="registerTimeRange">
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="首充时间" name="firstDepositTimeRange">
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="数据时间" name="dataTimeRange">
                <DatePicker.RangePicker style={{ width: '100%' }} defaultValue={[dayjs(), dayjs()]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} style={{ width: '100%', marginTop: 16 }}>
            <Col span={6}>
              <Form.Item name="memberId">
                <Input placeholder="请输入会员ID" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="superiorAgentId">
                <Input placeholder="请输入上级代理ID" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="superiorAgentAccount">
                <Input placeholder="请输入上级代理账号" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="memberAccount">
                <Input placeholder="请输入会员账号" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} style={{ width: '100%', marginTop: 16 }}>
            <Col span={6}>
              <Form.Item name="vipLevel">
                <Select placeholder="请选择VIP等级" allowClear>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
                    <Option key={level} value={level}>VIP{level}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="mobile">
                <Input placeholder="请输入手机号码" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="rechargeStatus">
                <Select placeholder="请选择状态" allowClear>
                  <Option value="yes">已充值</Option>
                  <Option value="no">未充值</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="rechargeGame">
                <Select placeholder="请选择充值" allowClear>
                  <Option value="recharge">充值</Option>
                  <Option value="game">游戏</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} style={{ width: '100%', marginTop: 16 }}>
            <Col span={6}>
              <Form.Item name="accountStatus">
                <Select placeholder="请选择状态" allowClear>
                  <Option value="normal">正常</Option>
                  <Option value="locked">锁定</Option>
                  <Option value="frozen">冻结</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="memberTags">
                <Select
                  mode="multiple"
                  placeholder="请选择标签"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  tagRender={(props) => {
                    const { label, value, closable, onClose } = props;
                    const tag = mockTags.find(t => t.id === value);
                    return (
                      <Tag
                        color={tag?.color}
                        closable={closable}
                        onClose={onClose}
                        style={{ marginRight: 3 }}
                      >
                        {label}
                      </Tag>
                    );
                  }}
                >
                  {mockTags.map(tag => (
                    <Option key={tag.id} value={tag.id}>
                      <Tag color={tag.color}>{tag.name}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="有充值记录且未投注天数≥" style={{ marginBottom: 0 }}>
                <Input.Group compact>
                  <InputNumber style={{ width: '40%' }} placeholder="请输入最小值" />
                  <span style={{ width: '10%', textAlign: 'center', lineHeight: '32px' }}>天≤</span>
                  <InputNumber style={{ width: '40%' }} placeholder="请输入最大值" />
                  <span style={{ width: '10%', textAlign: 'center', lineHeight: '32px' }}>天</span>
                </Input.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} style={{ width: '100%', marginTop: 16 }}>
            <Col span={24}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                  搜索
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  重置
                </Button>
                <Button type="default" icon={<ExportOutlined />} onClick={handleExport}>
                  导出
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>

        {/* 操作按钮 */}
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增
          </Button>
          <Button type="default" icon={<UnlockOutlined />} onClick={handleAllowLogin}>
            允许登录
          </Button>
          <Button danger icon={<LockOutlined />} onClick={handleForbidLogin}>
            禁止登录
          </Button>
          <Button type="default" icon={<DollarOutlined />} onClick={handleUnfreezeFunds}>
            资金解冻
          </Button>
          <Button danger icon={<DollarOutlined />} onClick={handleFreezeFunds}>
            资金冻结
          </Button>
          <Button type="default" icon={<TagsOutlined />} onClick={handleBatchTag}>
            批量打标签
          </Button>
        </Space>

        {/* 数据表格 */}
        <div style={{ marginBottom: 8, color: '#666' }}>[实时数据]</div>
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredMembers}
          rowKey="id"
          scroll={{ x: 2500 }}
          pagination={{
            total: filteredMembers.length,
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>

      {/* 编辑会员弹窗 */}
      <Modal
        title="编辑会员"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleSaveEdit}
        width={1200}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="用户信息" key="userInfo">
            <Form form={editForm} layout="vertical">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="会员账号" name="account">
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="会员ID" name="memberId">
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="会员昵称" name="nickname">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="会员邮箱" name="email">
                    <Input.Group compact>
                      <Input style={{ width: 'calc(100% - 60px)' }} placeholder="请输入" />
                      <Button>修改</Button>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="会员手机号码" name="mobile">
                    <Input.Group compact>
                      <Input style={{ width: 'calc(100% - 60px)' }} placeholder="请输入" />
                      <Button>修改</Button>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="真实姓名" name="realName">
                    <Input.Group compact>
                      <Input style={{ width: 'calc(100% - 60px)' }} placeholder="请输入" />
                      <Button>修改</Button>
                    </Input.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="所属层级" name="level">
                    <Input.Group compact>
                      <Select style={{ width: 'calc(100% - 60px)' }} placeholder="请选择" />
                      <Button>修改</Button>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="VIP等级" name="vipLevel">
                    <Input.Group compact>
                      <Select style={{ width: 'calc(100% - 60px)' }}>
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
                          <Option key={level} value={level}>VIP{level}</Option>
                        ))}
                      </Select>
                      <Button>修改</Button>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="是否锁定" name="isLocked">
                    <Input.Group compact>
                      <Radio.Group>
                        <Radio value={false}>否</Radio>
                        <Radio value={true}>是</Radio>
                      </Radio.Group>
                      <Button>修改</Button>
                    </Input.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="备注" name="remarks">
                    <Input.Group compact>
                      <Input style={{ width: 'calc(100% - 60px)' }} placeholder="请输入" />
                      <Button>修改</Button>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="账号状态" name="accountStatus">
                    <Select>
                      <Option value="normal">正常</Option>
                      <Option value="locked">锁定</Option>
                      <Option value="frozen">冻结</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="黑名单封禁" name="blacklistBan">
                    <Select placeholder="请选择状态" allowClear>
                      <Option value="yes">是</Option>
                      <Option value="no">否</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="登录密码" name="loginPassword">
                    <Input.Group compact>
                      <Input.Password style={{ width: 'calc(100% - 60px)' }} placeholder="请输入" />
                      <Button>修改</Button>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="提现密码" name="withdrawalPassword">
                    <Input.Group compact>
                      <Input.Password style={{ width: 'calc(100% - 60px)' }} placeholder="请输入" />
                      <Button>修改</Button>
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Button danger>场馆余额回收</Button>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <div>会员注册时间: {editingMember?.registerTime}</div>
                </Col>
                <Col span={8}>
                  <div>会员首充时间: {editingMember?.firstDepositTime || '-'}</div>
                </Col>
                <Col span={8}>
                  <Form.Item label="当前稽核流水" name="auditFlow">
                    <Input.Group compact>
                      <InputNumber style={{ width: 'calc(100% - 60px)' }} defaultValue={0} />
                      <Button>清除</Button>
                    </Input.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Divider orientation="left">代理信息</Divider>
              <Descriptions column={3} bordered>
                <Descriptions.Item label="上级代理">
                  {editingMember?.superiorAgentAccount} <a>修改</a>
                </Descriptions.Item>
                <Descriptions.Item label="上级代理ID">{editingMember?.superiorAgentId}</Descriptions.Item>
                <Descriptions.Item label="代理账号">{editingMember?.account}</Descriptions.Item>
                <Descriptions.Item label="层级">一级代理</Descriptions.Item>
                <Descriptions.Item label="直属下级">0</Descriptions.Item>
                <Descriptions.Item label="所有下级">0</Descriptions.Item>
              </Descriptions>
              <div style={{ marginTop: 16 }}>
                <div>提现账户: {editingMember?.account}</div>
                <a>查看银行修改历史</a>
              </div>
            </Form>
          </TabPane>
          <TabPane tab="会员标签" key="memberTags">
            <Form form={editForm} layout="vertical">
              <Form.Item label="选择标签" name="memberTags">
                <Checkbox.Group
                  onChange={(checkedValues) => {
                    // 实时更新表格数据
                    const selectedTags = (checkedValues as number[]).map(tagId => {
                      const tag = mockTags.find(t => t.id === tagId);
                      if (tag) {
                        const existingTag = editingMember?.tags?.find(t => t.tagId === tagId);
                        return {
                          tagId: tag.id,
                          tagName: tag.name,
                          tagColor: tag.color,
                          tagGroupName: tag.tagGroupName,
                          lastModifier: existingTag?.lastModifier || 'admin',
                          lastModifyTime: existingTag?.lastModifyTime || dayjs().format('YYYY-MM-DD HH:mm:ss'),
                        };
                      }
                      return null;
                    }).filter(Boolean) as MemberTag[];
                    
                    // 更新编辑中的会员数据
                    if (editingMember) {
                      setEditingMember({
                        ...editingMember,
                        tags: selectedTags,
                      });
                    }
                  }}
                >
                  <Row gutter={[16, 16]}>
                    {mockTags.map(tag => (
                      <Col key={tag.id} span={8}>
                        <Checkbox value={tag.id}>
                          <Tag color={tag.color}>{tag.name}</Tag>
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
              <Divider orientation="left">当前会员标签</Divider>
              <Table
                dataSource={editingMember?.tags || []}
                rowKey="tagId"
                pagination={false}
                columns={[
                  {
                    title: '标签名称',
                    dataIndex: 'tagName',
                    key: 'tagName',
                    render: (text: string, record: MemberTag) => (
                      <Tag color={record.tagColor}>{text}</Tag>
                    ),
                  },
                  {
                    title: '最后修改人',
                    dataIndex: 'lastModifier',
                    key: 'lastModifier',
                  },
                  {
                    title: '最后修改时间',
                    dataIndex: 'lastModifyTime',
                    key: 'lastModifyTime',
                  },
                  {
                    title: '操作',
                    key: 'action',
                    render: (_: any, record: MemberTag) => (
                      <Popconfirm
                        title="确认取消该标签？"
                        onConfirm={() => {
                          const currentTags = editForm.getFieldValue('memberTags') || [];
                          editForm.setFieldsValue({
                            memberTags: currentTags.filter((id: number) => id !== record.tagId),
                          });
                          // 更新编辑中的会员数据
                          if (editingMember) {
                            setEditingMember({
                              ...editingMember,
                              tags: editingMember.tags?.filter(t => t.tagId !== record.tagId),
                            });
                          }
                          message.success('标签已取消');
                        }}
                      >
                        <Button type="link" danger size="small">
                          取消
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            </Form>
          </TabPane>
        </Tabs>
      </Modal>

      {/* 批量打标签弹窗 */}
      <Modal
        title="批量打标签"
        open={batchTagModalVisible}
        onCancel={() => setBatchTagModalVisible(false)}
        onOk={handleBatchTagSubmit}
        width={800}
      >
        <Form form={editForm} layout="vertical">
          <div style={{ marginBottom: 16 }}>
            已选择 <strong>{selectedRowKeys.length}</strong> 个会员
          </div>
          <Form.Item label="选择标签" name="batchTags" rules={[{ required: true, message: '请至少选择一个标签' }]}>
            <Checkbox.Group>
              <Row gutter={[16, 16]}>
                {mockTags.map(tag => (
                  <Col key={tag.id} span={8}>
                    <Checkbox value={tag.id}>
                      <Tag color={tag.color}>{tag.name}</Tag>
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MemberList;

